import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { recruitmentService, menusService, staffService } from '../../services';
import {
  Modal, Select, Button, PageHeader, useEmailAvailability, UserPhoto,
} from '../../components';
import { RecruitmentSkeleton, RecruitmentPanelSkeleton, Skeleton as Shimmer } from '../../skeletons';
import { buildCandidateSummary } from '../../utils/candidateSummary';
import { JOB_TITLES } from '../../utils/jobTitles';
import PreInterviewPanel from './recruitment/PreInterviewPanel';
import PersonalDetailsPanel from './recruitment/PersonalDetailsPanel';
import TransferPanel from './recruitment/TransferPanel';
import InterviewPanelPicker from './recruitment/InterviewPanelPicker';
import { toast } from 'react-hot-toast';
import {
  UserPlus, Search, Send, Copy, Check, X, FileText, ShieldCheck,
  ClipboardCheck, CalendarClock, ArrowRight, Trash2, ExternalLink, Info,
  AlertCircle, UserCheck, Ban, RotateCcw, Archive, MoreVertical,
  User as UserIcon, Wand2, MapPin, Video, Mail,
} from 'lucide-react';

/**
 * Recruitment & Onboarding
 *
 * Sits inside Staff. Where "Add Staff" has the manager type a new starter's
 * whole record in by hand, this flow has the CANDIDATE fill in their own
 * details through two secure emailed links — the manager's job here is to read
 * what comes back, record their own decisions on it, and only at the very end
 * assign the user ID and PIN and transfer the record into the Staff Directory.
 *
 * Four steps, in order, because the order is the design — each asks only for
 * what is reasonable to ask at that point:
 *   1 Pre-interview     the candidate's CV, Right to Work, NI, DBS if held,
 *                       employment history and referees, plus the manager's own
 *                       verdict on them (see recruitment/PreInterviewPanel.js)
 *   2 Interview         the booking, panel, assessment and decision
 *   3 Personal details  address, next of kin and bank details — asked only
 *                       after somebody has decided to hire them
 *   4 Transfer to staff the start date, onboarding plan and the transfer itself
 *
 * The step shown against each candidate is derived by the backend from real
 * progress, so it can never disagree with what's on screen.
 *
 * Backed by /api/carehome-manager/recruitment.
 */

const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

// The four derived steps plus the two the manager sets by hand. Keyed by the
// exact stage strings the API returns (utils/recruitment.js) — a legacy name is
// translated server-side before it ever reaches here.
const STAGE_META = {
  'Pre-interview':     { cls: 'bg-slate-100 dark:bg-[#31373f] text-slate-600 dark:text-gray-300' },
  Interview:           { cls: 'bg-violet-100 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300' },
  'Personal details':  { cls: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' },
  'Ready to transfer': { cls: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' },
  'On hold':           { cls: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400' },
  Unsuccessful:        { cls: 'bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400' },
  Withdrawn:           { cls: 'bg-slate-200 dark:bg-[#3a4049] text-slate-700 dark:text-gray-300' },
  'Not proceeding':    { cls: 'bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400' },
  // Not a stage the API stores — History's label for a hire who went through
  // to the Staff Directory (see loadTransferred in recruitment.controller.js).
  Transferred:         { cls: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' },
};

/**
 * Why an application was archived. An archived candidate's STAGE is the reason,
 * so these are stage names too — the same list the API validates against
 * (ARCHIVE_REASONS in api/utils/recruitment.js).
 */
const ARCHIVE_REASONS = ['Unsuccessful', 'Withdrawn', 'Not proceeding'];

/** The stages a manager sets by hand — sticky, and they pause progress. */
const MANUAL_STAGES = ['On hold', ...ARCHIVE_REASONS];

/** Today, as a date input wants it. The archive date defaults to it. */
const todayInput = () => new Date().toISOString().slice(0, 10);

const SOURCE_OPTIONS = [
  { value: 'Direct application', label: 'Direct application' },
  { value: 'Indeed', label: 'Indeed' },
  { value: 'Referral', label: 'Referral' },
  { value: 'Recruitment agency', label: 'Recruitment agency' },
  { value: 'Other', label: 'Other' },
];

const OUTCOME_OPTIONS = ['Pending', 'Successful', 'Unsuccessful', 'Withdrawn']
  .map((value) => ({ value, label: value }));

/*
 * There is no Carer question when a candidate is added.
 *
 * Whether somebody ends up a carer or management is decided at the TRANSFER,
 * where the same StaffAccessPicker the Add Staff form uses asks it — by then
 * there has been an interview to base it on. Asking at the point of typing a
 * name is asking before anybody knows, and the answer then quietly decides
 * their role id months later.
 */

/**
 * The four steps, shown as a numbered progress header rather than tabs.
 *
 * They are still freely clickable — a manager often needs to look back at what
 * a candidate sent while recording an interview decision — but numbering them
 * says what tabs could not: this is an order, and step 3 asks for things it
 * would be wrong to ask for at step 1.
 *
 * `stage` is the stage name the API derives when a candidate is sitting on that
 * step, which is what marks earlier steps complete.
 */
const STEPS = [
  { id: 'pre-interview', label: 'Pre-interview', stage: 'Pre-interview' },
  { id: 'interview', label: 'Interview', stage: 'Interview' },
  { id: 'personal', label: 'Personal details', stage: 'Personal details' },
  { id: 'transfer', label: 'Transfer to staff', stage: 'Ready to transfer' },
];

const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Date inputs need YYYY-MM-DD; the API may hand back a full ISO timestamp.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

/** Which candidate list is on screen: the view, search and stage filter it was loaded with. */
const listKey = (view, search, stage) => `${view}|${String(search || '').trim()}|${stage}`;

/** How an interview happens — the API validates against the same list (INTERVIEW_MODES). */
const INTERVIEW_MODES = [
  { value: 'In person', icon: MapPin },
  { value: 'Online', icon: Video },
];

/** The fields an invitation email is built from; changing one after it is sent makes it out of date. */
const ARRANGEMENT_FIELDS = ['interview_date', 'interview_time', 'interview_mode', 'interview_address', 'interview_link', 'interview_instructions'];

/** Same rule as the API's isMeetingLink: a full http(s) address the candidate can open. */
const isMeetingLink = (value) => {
  try {
    const url = new URL(String(value || '').trim());
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

/** What the invitation still needs, worded for "Add … to send it". Mirrors interviewInvitationMissing. */
const invitationMissing = (form) => [
  !form.interview_date && 'the date',
  !form.interview_time && 'the time',
  !form.interview_mode && 'in person or online',
  form.interview_mode === 'In person' && !form.interview_address.trim() && 'the address',
  form.interview_mode === 'Online' && !isMeetingLink(form.interview_link) && 'a meeting link',
].filter(Boolean);

/** The three outcome emails, as the manager sees them before sending. */
const OUTCOME_EMAIL = {
  Successful: {
    label: 'Successful interview email',
    summary: 'Tells them their interview was successful and that you will be in touch with the next steps, including the personal details form.',
  },
  Unsuccessful: {
    label: 'Unsuccessful interview email',
    summary: 'Thanks them for interviewing and tells them their application will not go further this time. No reason is given — your panel notes are never included.',
  },
  Withdrawn: {
    label: 'Application withdrawn email',
    summary: 'Confirms their application has been withdrawn and closed, and that they can reply if this is a mistake.',
  },
};

/** "Tuesday 13 October 2026" from a stored YYYY-MM-DD, as the invitation prints it. */
const longDate = (v) => {
  if (!v) return '';
  const d = new Date(`${String(v).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

/** "2:30pm" from a stored HH:MM. */
const shortTime = (v) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || ''));
  if (!m) return v || '';
  const hour = Number(m[1]);
  return `${hour % 12 || 12}:${m[2]}${hour < 12 ? 'am' : 'pm'}`;
};

/**
 * The interview form as stored on a candidate. Used to reset the form when
 * switching candidate, and as the baseline "unsaved changes" is measured from.
 */
const interviewFormFrom = (details = {}) => ({
  interview_date: toDateInput(details.interview_date),
  interview_time: details.interview_time || '',
  // Stored as a list of names. An older record may hold one comma-separated
  // string, which is split back out rather than shown as a single panellist
  // called "Steven, Amira".
  interview_panel: Array.isArray(details.interview_panel)
    ? details.interview_panel
    : String(details.interview_panel || '').split(',').map((part) => part.trim()).filter(Boolean),
  interview_assessment: details.interview_assessment || '',
  interview_score: details.interview_score || '',
  interview_decision: details.interview_decision || 'Pending',
  // NOTHING IS AUTOFILLED HERE. This box holds what the PANEL decided and
  // why, so it starts empty and stays empty until somebody types in it.
  //
  // It briefly opened on the recruiter's notes. That was wrong in a way
  // worth recording: pressing Save then stored one person's pre-interview
  // note as the panel's own findings, and an interview record that nobody
  // actually wrote reads exactly like one they did. The recruiter's notes
  // are shown beside this box instead, read-only, under "Notes for the
  // interview".
  interview_outcome: details.interview_outcome || '',
  interview_mode: details.interview_mode || '',
  interview_address: details.interview_address || '',
  interview_link: details.interview_link || '',
  interview_instructions: details.interview_instructions || '',
});

// Uploaded files are stored as /api/files/... paths.
const fileHref = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_ORIGIN}${url}`);

const inputCls = 'w-full px-4 py-2.5 border border-[#CBD5E1] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px]';
const fieldLabelCls = 'block text-[11.5px] font-bold uppercase tracking-wide text-[#64748B] dark:text-gray-400 mb-1.5';
const cardCls = 'bg-white dark:bg-[#262b33] border border-[#E3E9F3] dark:border-gray-700 rounded-2xl';

/** The small status pill in a card's top-right corner. */
const CardPill = ({ tone = 'neutral', children }) => {
  const tones = {
    neutral: 'bg-slate-100 dark:bg-[#31373f] text-slate-600 dark:text-gray-300',
    good: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
    warn: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
  };
  return <span className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md shrink-0 ${tones[tone]}`}>{children}</span>;
};

/** Standard card header: title, subtitle, and a status pill on the right. */
const CardHead = ({ title, subtitle, pill }) => (
  <div className="flex items-start justify-between gap-4 mb-5">
    <div className="min-w-0">
      <h3 className="text-[15.5px] font-bold text-[#0B1220] dark:text-gray-100">{title}</h3>
      {subtitle && <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">{subtitle}</p>}
    </div>
    {pill}
  </div>
);

/** One interview email on step 2: what it is, where it stands, and its button. */
const InterviewEmailRow = ({ icon: Icon, title, status, tone = 'neutral', action = null }) => {
  const tones = {
    neutral: 'text-[#64748B] dark:text-gray-400',
    good: 'text-emerald-700 dark:text-emerald-400',
    warn: 'text-amber-700 dark:text-amber-400',
  };
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3.5 border-b border-[#F1F5F9] dark:border-gray-800 last:border-b-0">
      <div className="w-9 h-9 rounded-xl bg-[#EEF3FF] dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-300 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100">{title}</div>
        <div className={`text-[12.5px] mt-0.5 ${tones[tone]}`}>{status}</div>
      </div>
      {action}
    </div>
  );
};

const StagePill = ({ stage }) => (
  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${STAGE_META[stage]?.cls || STAGE_META['Pre-interview'].cls}`}>
    {String(stage || '').toUpperCase()}
  </span>
);

/**
 * A hire that went through to the Staff Directory, as History shows it.
 *
 * Read-only and deliberately small: they are staff now, so the candidate
 * record's steps and forms no longer apply, and their documents were copied
 * to their staff file at transfer. The staff record is one click away.
 */
const TransferredRecord = ({ candidate, onOpenStaff }) => {
  const t = candidate.transferred || {};
  return (
    <div className="p-5 space-y-4">
      <div className="flex items-start gap-3 min-w-0">
        <UserPhoto
          src={candidate.avatar_url}
          alt={candidate.name}
          className="w-12 h-12 rounded-full"
          fallback={(
            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-[15px] font-bold shrink-0">
              {candidate.initials || '?'}
            </div>
          )}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-[19px] font-bold text-[#0B1220] dark:text-gray-100 break-words min-w-0">{candidate.name}</h2>
            <StagePill stage="Transferred" />
          </div>
          <div className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">
            {candidate.job_title ? `${candidate.job_title} · ` : ''}
            Applied {fmtDate(candidate.applied_at) || '—'}
            {candidate.source ? ` · via ${candidate.source}` : ''}
          </div>
          <div className="text-[13px] text-[#64748B] dark:text-gray-400">
            {candidate.email}{candidate.phone ? ` · ${candidate.phone}` : ''}
          </div>
        </div>
      </div>

      <div className="p-4 rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-900/10">
        <div className="flex items-center gap-2 text-[13.5px] font-bold text-emerald-800 dark:text-emerald-300">
          <UserCheck className="w-4 h-4" /> Transferred to the Staff Directory
        </div>
        <div className="text-[13px] text-[#334155] dark:text-gray-300 mt-1.5">
          {t.at ? `Transferred ${fmtDateTime(t.at)}` : 'Transfer date not recorded'}
          {t.by ? ` · by ${t.by}` : ''}
        </div>
        <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1">
          {t.login_id ? `Login ID ${t.login_id} · ` : ''}Staff status now: {t.staff_status || '—'}
        </div>
        <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">
          Their recruitment documents were copied to their staff file. Everything about them is now managed in Staff Management.
        </p>
        <Button
          variant="secondary" size="sm" className="mt-3" rightIcon={<ArrowRight className="w-4 h-4" />}
          onClick={() => onOpenStaff(candidate.id)}
        >
          Open staff record
        </Button>
      </div>
    </div>
  );
};

/**
 * A tickable check / onboarding task row, with a MANAGER badge making it explicit
 * that this is not something the candidate can complete for themselves.
 */
/** One row of a definition list. */
const SummaryRow = ({ label, value, muted }) => (
  <div className="flex items-start justify-between gap-6 py-3.5 border-b border-[#F1F5F9] dark:border-gray-800 last:border-b-0">
    <span className="text-[11.5px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500 shrink-0 max-w-[45%]">{label}</span>
    <span className={`text-[13.5px] text-right ${muted ? 'text-[#94A3B8] dark:text-gray-500 italic' : 'font-semibold text-[#0B1220] dark:text-gray-100'}`}>
      {value}
    </span>
  </div>
);

const Recruitment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [candidates, setCandidates] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  // Only the very first fetch gets the full-page shimmer. Searching and
  // filtering also set `loading`, and swapping the whole page out on every
  // keystroke would make the page flash.
  const [firstLoad, setFirstLoad] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  // 'active' is the worklist, 'history' the closed applications. Two lists
  // rather than one, because a worklist whose count only grows stops being one.
  const [view, setView] = useState('active');
  const [viewCounts, setViewCounts] = useState({ active: 0, history: 0 });
  // The filter options come from the API so the list of choices and the thing
  // that filters by them cannot drift apart.
  const [filters, setFilters] = useState([]);

  const [activeId, setActiveId] = useState(null);

  // A link to one candidate — ?candidate=<id>&view=history — from Ask Patch.
  // The candidate is selected once the list it lives in has loaded: the list
  // that happens to be on screen first (another tab, a search) would otherwise
  // hand the selection to its own first row.
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingCandidate = useRef(null); // { id, list }
  const [loadedList, setLoadedList] = useState(null);
  const [active, setActive] = useState(null);       // full candidate record
  const [loadingActive, setLoadingActive] = useState(false);
  const [activeStep, setActiveStep] = useState('pre-interview');
  const [busy, setBusy] = useState(false);

  // The option lists and document groups the backend validates against —
  // Right to Work provisions and types, DBS availability, the manager check
  // outcomes. Fetched rather than hardcoded so a label can be changed in
  // api/utils/recruitment.js alone and both sides agree.
  const [definitions, setDefinitions] = useState(null);

  // Modals
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  // Archiving is per-candidate rather than "the open one": the ⋮ menu on the
  // list archives whoever it belongs to, which is not necessarily the candidate
  // currently open in the pane.
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [reopenTarget, setReopenTarget] = useState(null);
  // Which row's ⋮ menu is open, by candidate id.
  const [menuFor, setMenuFor] = useState(null);
  const [showSubmission, setShowSubmission] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  const [addForm, setAddForm] = useState({
    name: '', email: '', phone: '', job_title: '',
    source: 'Direct application', interview_date: '', notes: '',
  });
  // Checked as they finish typing, so "already in use" is answered beside the
  // box rather than by a rejected save.
  const emailCheck = useEmailAvailability(addForm.email);
  const [inviteForm, setInviteForm] = useState({ expiry_days: '7', message: '' });
  const [archiveForm, setArchiveForm] = useState({ reason: 'Unsuccessful', date: todayInput(), note: '' });
  const [reopenReason, setReopenReason] = useState('');

  // The interview record — edited locally, saved explicitly.
  const [interviewForm, setInterviewForm] = useState(() => interviewFormFrom());
  // The invitation / outcome email about to be sent: { type, message }.
  const [emailDialog, setEmailDialog] = useState(null);

  // The dashboard modules a manager can grant, and the teams a roster can be
  // attached to. Loaded here rather than in the transfer panel so the panel
  // stays a view — and so switching candidate doesn't refetch them.
  const [availableMenus, setAvailableMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);
  const [groups, setGroups] = useState([]);
  // The organisation's own people, for the interview panel picker. Names only:
  // the record has to keep reading correctly after somebody leaves.
  const [staffList, setStaffList] = useState([]);
  // A domiciliary service records availability; a care home rosters shifts.
  // Read from the organisation, the same axis Add Staff branches on.
  const isDomiciliaryOrg = (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';

  const loadCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const params = { view };
      if (search.trim()) params.search = search.trim();
      if (stageFilter !== 'all') params.stage = stageFilter;

      const response = await recruitmentService.list(params);
      if (response.success) {
        setCandidates(response.data || []);
        setLoadedList(listKey(view, search, stageFilter));
        setKpis(response.kpis || null);
        setViewCounts(response.views || { active: 0, history: 0 });
        // Sent on every list response, so a filter added on the server appears
        // here without a deploy of this file.
        if (response.filters?.length) setFilters(response.filters);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load candidates');
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  }, [search, stageFilter, view]);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(loadCandidates, 300);
    return () => clearTimeout(timer);
  }, [loadCandidates]);

  useEffect(() => {
    recruitmentService.definitions()
      .then((response) => { if (response.success) setDefinitions(response.data); })
      // Not fatal: the panel falls back to showing the stored values without
      // their friendly labels rather than failing to render.
      .catch(() => {});
  }, []);

  useEffect(() => {
    menusService.getUserMenus()
      .then((response) => {
        if (response.success) {
          setAvailableMenus((response.data.allMenus || []).filter((menu) => !menu.is_admin));
        }
      })
      .catch(() => { /* the transfer dialog says so if menus are unavailable */ })
      .finally(() => setLoadingMenus(false));

    staffService.getStaffGroups()
      .then((response) => { if (response?.success) setGroups(response.data || []); })
      .catch(() => { /* a team is optional on a roster */ });

    // Who can be put on an interview panel. Active staff only — a leaver is not
    // going to sit on tomorrow's panel, and anyone already recorded on a past
    // one keeps their place regardless (see InterviewPanelPicker).
    staffService.getAllStaff(1, 200, '', '', 'active', false)
      .then((response) => {
        if (response?.success) {
          setStaffList((response.data || []).map((row) => ({ id: row.id, name: row.name })));
        }
      })
      .catch(() => { /* the picker says so when there is nobody to choose */ });
  }, []);

  /**
   * Reset the locally-edited forms from a candidate record.
   *
   * Only called when SWITCHING candidate, never on a progress update — otherwise
   * ticking a checkbox would silently discard whatever the manager had typed into
   * the interview or employment-terms fields without saving.
   */
  const resetForms = useCallback((candidate) => {
    setInterviewForm(interviewFormFrom(candidate.details || {}));
    setEmailDialog(null);
  }, []);

  const loadCandidate = useCallback(async (candidateId) => {
    try {
      setLoadingActive(true);
      const response = await recruitmentService.get(candidateId);
      if (response.success) {
        setActive(response.data);
        resetForms(response.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load this candidate');
    } finally {
      setLoadingActive(false);
    }
  }, [resetForms]);

  // Arriving from a link to one candidate: switch to the list that holds them,
  // unfiltered, and tidy the address bar so a refresh doesn't repeat it.
  useEffect(() => {
    const id = Number(searchParams.get('candidate'));
    if (!id) return;
    const wantView = searchParams.get('view') === 'history' ? 'history' : 'active';
    const list = listKey(wantView, '', 'all');
    // Already showing that list (nothing will reload): select them now.
    if (loadedList === list && candidates.some((candidate) => candidate.id === id)) {
      pendingCandidate.current = null;
      setActiveId(id);
    } else {
      pendingCandidate.current = { id, list };
    }
    setView(wantView);
    setSearch('');
    setStageFilter('all');
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  // Select the first candidate once the list arrives, so the detail pane is never
  // blank when there is something to show — unless a link asked for one.
  useEffect(() => {
    const pending = pendingCandidate.current;
    if (pending) {
      if (loadedList !== pending.list) return; // their list hasn't loaded yet
      pendingCandidate.current = null;
      if (candidates.some((candidate) => candidate.id === pending.id)) {
        setActiveId(pending.id);
        return;
      }
    }
    if (!candidates.length) { setActiveId(null); setActive(null); return; }
    if (!activeId || !candidates.some((candidate) => candidate.id === activeId)) {
      setActiveId(candidates[0].id);
    }
  }, [candidates, activeId, loadedList]);

  // A transferred hire (History) is staff now and has no candidate record to
  // fetch — the list row carries everything their pane shows.
  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;
  useEffect(() => {
    if (!activeId) return;
    const row = candidatesRef.current.find((candidate) => candidate.id === activeId);
    if (row?.transferred) { setActive(row); return; }
    loadCandidate(activeId);
  }, [activeId, loadCandidate]);

  /**
   * Every write returns the updated candidate; fold it into both the detail pane
   * and the list row so the stage pill and progress counts move immediately.
   * Deliberately does NOT touch the local forms — see resetForms.
   */
  const applyUpdate = (candidate) => {
    // Only ever replaces the pane when the write was about the candidate IN it.
    // The ⋮ menu on the list acts on whoever it belongs to, which is often not
    // the one open — and swapping the pane out from under the manager because
    // they archived somebody else would be its own bug.
    setActive((prev) => (prev && prev.id !== candidate.id ? prev : candidate));
    setCandidates((prev) => prev.map((row) => (row.id === candidate.id ? { ...row, ...candidate } : row)));
  };

  const run = async (fn, { successMessage } = {}) => {
    try {
      setBusy(true);
      const response = await fn();
      if (response?.success) {
        if (response.data?.checks) applyUpdate(response.data);
        // A request can succeed while the email it triggered did not. Endpoints
        // that send mail report that separately on `emailed`; show a failed
        // delivery as a failure rather than a green success toast, or the
        // manager walks away believing the candidate was contacted.
        const emailed = response.emailed ?? response.data?.emailed;
        const message = successMessage || response.message;
        if (message) (emailed === false ? toast.error : toast.success)(message);
        return response;
      }
      toast.error(response?.message || 'Something went wrong');
      return null;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Something went wrong');
      return null;
    } finally {
      setBusy(false);
    }
  };

  /** Sample candidate, matching the Add Staff form's "Fill Test Data" helper. */
  const fillTestCandidate = () => {
    const seed = Math.floor(1000 + Math.random() * 9000);
    const interview = new Date();
    interview.setDate(interview.getDate() + 7);
    setAddForm({
      name: `Alex Taylor ${seed}`,
      email: `alex.${seed}@example.com`,
      phone: `+44 7700 9${String(seed).slice(0, 2)}42`,
      role_id: '6',
      job_title: 'Care Assistant',
      source: 'Indeed',
      interview_date: interview.toISOString().slice(0, 10),
      notes: 'Test candidate. Available immediately, 4 years care experience.',
    });
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) return toast.error('Enter the candidate\'s name');
    if (!addForm.email.trim()) return toast.error('Enter an email address so the request can be sent');
    // The API refuses it too; this only saves the round trip and keeps the
    // message next to the box that caused it.
    if (emailCheck.state === 'taken') return toast.error(emailCheck.message);

    // The candidate's provisional role, sent as the Carer choice so the API
    // picks the right carer role for this organisation's type. Confirmed (and
    // possibly changed) by the Carer choice on the transfer step, which is what
    // finally decides it.
    const response = await run(() => recruitmentService.create({
      ...addForm,
      is_carer: addForm.is_carer === 'yes',
    }));
    if (!response) return;

    setShowAdd(false);
    setAddForm({
      name: '', email: '', phone: '', job_title: '',
      is_carer: 'no', source: 'Direct application', interview_date: '', notes: '',
    });
    await loadCandidates();
    setActiveId(response.data.id);
    // Step 1 — where an unfamiliar candidate is read from. 'overview' stopped
    // existing when the tabs became four numbered steps, and left the pane blank.
    setActiveStep('pre-interview');
  };

  const handleSendLink = async () => {
    const response = await run(() => recruitmentService.sendFormLink(active.id, {
      expiry_days: Number(inviteForm.expiry_days),
      message: inviteForm.message,
    }));
    if (!response) return;

    setLinkResult(response.data);
    setShowInvite(false);
    await loadCandidate(active.id);
    await loadCandidates();
  };

  /**
   * Save the interview record. Returns the saved candidate, or null.
   *
   * With `offerEmail` (the Save button), a save that has just made an email
   * due opens it ready to send: a new outcome → the outcome email; new or
   * changed arrangements on an undecided interview → the invitation. The
   * manager still presses Send — saving never emails anyone by itself.
   */
  const handleSaveInterview = async ({ offerEmail = true } = {}) => {
    const form = interviewForm;
    if (form.interview_mode === 'Online' && form.interview_link.trim() && !isMeetingLink(form.interview_link)) {
      toast.error('Enter the meeting link as a full web address, starting https://');
      return null;
    }
    const before = interviewFormFrom(active.details || {});

    const response = await run(() => recruitmentService.update(active.id, {
      userDetails: {
        ...form,
        interview_address: form.interview_address.trim(),
        interview_link: form.interview_link.trim(),
        interview_instructions: form.interview_instructions.trim(),
      },
    }), { successMessage: 'Interview record saved' });
    if (!response) return null;
    loadCandidates();

    const saved = response.data;
    // The server tidies some values (a meeting link comes back normalised), so
    // the form follows what was stored — otherwise it reads as unsaved.
    setInterviewForm(interviewFormFrom(saved.details || {}));

    const emails = saved.interview_emails;
    if (offerEmail && emails && saved.email) {
      const decisionChanged = form.interview_decision !== before.interview_decision;
      const arrangementsChanged = ARRANGEMENT_FIELDS.some((field) => form[field] !== before[field]);
      if (decisionChanged && emails.outcome && !emails.outcome.sent) {
        setEmailDialog({ type: 'outcome', message: '' });
      } else if (arrangementsChanged && saved.interview_decision === 'Pending'
          && !MANUAL_STAGES.includes(saved.stage) && !emails.invitation.missing.length
          && (!emails.invitation.sent || emails.invitation.changed)) {
        setEmailDialog({ type: 'invitation', message: '' });
      }
    }
    return saved;
  };

  /** Open an interview email, saving the form first so the email matches it. */
  const openInterviewEmail = async (type) => {
    let candidate = active;
    if (!locked && interviewDirty) {
      candidate = await handleSaveInterview({ offerEmail: false });
      if (!candidate) return;
    }
    if (type === 'outcome' && !candidate.interview_emails?.outcome) {
      toast.error('Save the interview outcome as Successful, Unsuccessful or Withdrawn first');
      return;
    }
    setEmailDialog({ type, message: '' });
  };

  const handleSendInterviewEmail = async () => {
    const response = await run(() => recruitmentService.sendInterviewEmail(active.id, emailDialog.type, emailDialog.message));
    if (!response) return;
    setEmailDialog(null);
    loadCandidates();
  };

  /**
   * Email a set-PIN link — the same page the forgotten-PIN flow uses. Also
   * assigns the automatic user ID (first name + 3 digits) if the candidate is
   * still on their placeholder, since a PIN needs an ID to belong to.
   */
  const handleSendPinLink = async () => {
    const response = await run(() => recruitmentService.sendPinLink(active.id));
    if (response?.data?.candidate) applyUpdate(response.data.candidate);
  };

  /** Open the archive dialog for one candidate, from the list or the header. */
  const openArchive = (candidate) => {
    setMenuFor(null);
    setArchiveForm({ reason: 'Unsuccessful', date: todayInput(), note: '' });
    setArchiveTarget(candidate);
  };

  const openReopen = (candidate) => {
    setMenuFor(null);
    setReopenReason('');
    setReopenTarget(candidate);
  };

  const handleArchive = async () => {
    const response = await run(
      () => recruitmentService.archive(archiveTarget.id, archiveForm),
      { successMessage: `${archiveTarget.name} archived` },
    );
    if (!response) return;
    setArchiveTarget(null);
    // run() has already folded the updated record into the pane and the row.
    await loadCandidates();
  };

  const handleReopen = async () => {
    const response = await run(
      () => recruitmentService.reopen(reopenTarget.id, reopenReason),
      { successMessage: `${reopenTarget.name} is back in the active list` },
    );
    if (!response) return;
    setReopenTarget(null);
    setReopenReason('');
    await loadCandidates();
  };

  const handleDelete = async (candidate) => {
    if (!window.confirm(`Delete ${candidate.name}? Their application and uploaded documents will be removed permanently.`)) return;
    const response = await run(() => recruitmentService.remove(candidate.id));
    if (!response) return;
    setActiveId(null);
    setActive(null);
    await loadCandidates();
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select the link and copy it manually');
    }
  };

  // "Locked" means the application is paused or archived: everything is
  // readable, nothing is writable, until the manager resumes or reopens it.
  const locked = MANUAL_STAGES.includes(active?.stage);
  const archived = Boolean(active?.archive?.archived);
  // Interview emails are built from the SAVED record, so the step needs to
  // know when what is on screen isn't saved yet.
  const interviewDirty = Boolean(active)
    && JSON.stringify(interviewForm) !== JSON.stringify(interviewFormFrom(active.details || {}));

  /**
   * Step 2's second card: the invitation and the outcome email, each with its
   * own state and button, and every email sent so far underneath.
   */
  const renderInterviewEmails = () => {
    const emails = active?.interview_emails || {};
    const invitation = emails.invitation || {};
    const savedOutcome = emails.outcome || null;
    const noEmail = !active?.email;
    const missing = invitationMissing(interviewForm);
    const listWords = (items) => (items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0]);

    const savedForm = interviewFormFrom(active?.details || {});
    const editingArrangements = ARRANGEMENT_FIELDS.some((field) => interviewForm[field] !== savedForm[field]);

    let invite;
    if (invitation.sent && editingArrangements && !missing.length) {
      invite = { tone: 'warn', label: 'Save & send update', status: 'You have changed the interview details. Save them and send the candidate an update.' };
    } else if (invitation.sent && invitation.changed) {
      invite = { tone: 'warn', label: 'Send update', status: `The details changed after it was sent on ${fmtDateTime(invitation.sent.sent_at)}. Send an update so the candidate has the right ones.` };
    } else if (invitation.sent) {
      invite = { tone: 'good', label: 'Resend', status: `Sent ${fmtDateTime(invitation.sent.sent_at)} by ${invitation.sent.by}` };
    } else if (missing.length) {
      invite = { tone: 'neutral', label: 'Send invitation', status: `Add ${listWords(missing)} to send it.` };
    } else {
      invite = { tone: 'neutral', label: 'Send invitation', status: interviewDirty ? 'Ready — your changes are saved first.' : 'Ready to send.' };
    }

    // While the application is open, the outcome follows the form (it is saved
    // before sending). Once locked — recording Unsuccessful or Withdrawn is what
    // archives it — only the saved outcome can be emailed.
    const formOutcome = OUTCOME_EMAIL[interviewForm.interview_decision] ? interviewForm.interview_decision : null;
    const outcomeDecision = locked ? savedOutcome?.decision : formOutcome;
    const outcomeSent = savedOutcome && savedOutcome.decision === outcomeDecision ? savedOutcome.sent : null;
    const log = Array.isArray(emails.log) ? emails.log : [];

    return (
      <div className={`${cardCls} p-5`}>
        <CardHead
          title="Emails to the candidate"
          subtitle={noEmail ? 'No email address on file — add one to the candidate to email them.' : `Sent to ${active.email}, from the saved interview record.`}
        />

        <InterviewEmailRow
          icon={CalendarClock}
          title="Interview invitation"
          tone={invite.tone}
          status={invite.status}
          action={(
            <Button size="sm" variant={invitation.sent && !invitation.changed ? 'secondary' : 'primary'}
              leftIcon={<Send className="w-3.5 h-3.5" />}
              disabled={busy || locked || noEmail || missing.length > 0}
              onClick={() => openInterviewEmail('invitation')}>
              {invite.label}
            </Button>
          )}
        />

        {outcomeDecision ? (
          <InterviewEmailRow
            icon={Mail}
            title={OUTCOME_EMAIL[outcomeDecision].label}
            tone={outcomeSent ? 'good' : 'neutral'}
            status={outcomeSent
              ? `Sent ${fmtDateTime(outcomeSent.sent_at)} by ${outcomeSent.by}`
              : `Not sent yet. ${OUTCOME_EMAIL[outcomeDecision].summary}`}
            action={(
              <Button size="sm" variant={outcomeSent ? 'secondary' : 'primary'}
                leftIcon={<Send className="w-3.5 h-3.5" />}
                disabled={busy || noEmail}
                onClick={() => openInterviewEmail('outcome')}>
                {outcomeSent ? 'Resend' : 'Send email'}
              </Button>
            )}
          />
        ) : (
          <InterviewEmailRow
            icon={Mail}
            title="Outcome email"
            status="Record the outcome as Successful, Unsuccessful or Withdrawn and the matching email is drafted for you to send."
          />
        )}

        {log.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#F1F5F9] dark:border-gray-800">
            <div className={`${fieldLabelCls} mb-2`}>Sent so far</div>
            <ul className="space-y-1.5">
              {log.map((row, i) => (
                <li key={`${row.sent_at}-${i}`} className="flex flex-wrap items-baseline gap-x-2 text-[12.5px]">
                  <span className="font-semibold text-[#0B1220] dark:text-gray-100">
                    {row.type === 'invitation'
                      ? (row.updated ? 'Updated interview invitation' : 'Interview invitation')
                      : (OUTCOME_EMAIL[row.decision]?.label || 'Outcome email')}
                  </span>
                  <span className="text-[#64748B] dark:text-gray-400">{fmtDateTime(row.sent_at)} · {row.by}</span>
                  {!row.emailed && <span className="font-semibold text-rose-600 dark:text-rose-400">Not delivered</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };
  const nextAction = active?.next_action;

  // The transfer step follows the personal details form; the backend enforces
  // the same order, so this only saves the manager a rejected request.
  // One tile per step, so the row reads as the pipeline it is, plus the single
  // number that is about somebody else rather than about the manager: how many
  // candidates have a form out that hasn't come back.
  //
  // `filter` is the stage-filter value each tile stands for, so clicking a
  // number shows the candidates it counted. The API counts every tile THROUGH
  // that same filter, so the two can't disagree.
  const kpiTiles = useMemo(() => ([
    { label: 'Candidates', value: kpis?.total ?? 0, icon: UserPlus, cls: 'text-blue-600', filter: 'all' },
    { label: 'Awaiting candidate', value: kpis?.awaiting_candidate ?? 0, icon: CalendarClock, cls: 'text-amber-600', filter: 'awaiting_candidate' },
    { label: 'Interview', value: kpis?.interview ?? 0, icon: ShieldCheck, cls: 'text-violet-600', filter: 'Interview' },
    { label: 'Personal details', value: kpis?.personal_details ?? 0, icon: ClipboardCheck, cls: 'text-indigo-600', filter: 'Personal details' },
    { label: 'Ready to transfer', value: kpis?.ready ?? 0, icon: UserCheck, cls: 'text-emerald-600', filter: 'Ready to transfer' },
  ]), [kpis]);

  /**
   * Clicking a tile filters the list to what it counted.
   *
   * Clicking the one already applied clears it, so the same click both puts a
   * filter on and takes it off — a tile that only ever narrowed would leave the
   * manager hunting for the way back. The tiles count ACTIVE candidates, so a
   * click from History switches back to the list the number came from.
   */
  const applyKpiFilter = (filter) => {
    setView('active');
    setStageFilter((current) => (current === filter ? 'all' : filter));
  };

  if (firstLoad) return <RecruitmentSkeleton />;

  return (
    // pb-8 is plain bottom breathing room, as on every other page. It was
    // pb-28 while the Ask Patch launcher sat in the bottom-right corner and a
    // card's Save button could end up underneath it.
    <div className="space-y-5 pb-8">
      <PageHeader
        title="Recruitment & Onboarding"
        count={kpis?.total}
        subtitle="Manage candidates from interview through checks, onboarding and transfer to the Staff Directory."
        actions={(
          <Button variant="primary" leftIcon={<UserPlus className="w-4 h-4" />} onClick={() => setShowAdd(true)}>
            Add Candidate
          </Button>
        )}
      />

      {/* KPI tiles — each one is the filter it counts. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {kpiTiles.map((tile) => {
          // "Candidates" is the whole list rather than a filter of it, so it
          // never reads as selected — it is the way back, and clicking it
          // clears whatever is applied.
          const selected = view === 'active' && tile.filter !== 'all' && stageFilter === tile.filter;
          return (
            <button
              key={tile.label}
              type="button"
              aria-pressed={selected}
              title={selected ? 'Showing these — click to clear' : `Show ${tile.label.toLowerCase()}`}
              onClick={() => applyKpiFilter(tile.filter)}
              className={`${cardCls} p-4 text-left transition-colors ${
                selected
                  ? 'ring-2 ring-blue-500/70 border-blue-300 dark:border-blue-500/60 bg-[#F5F9FF] dark:bg-blue-900/10'
                  : 'hover:border-[#CBD5E1] dark:hover:border-gray-600 hover:bg-[#F8FAFC] dark:hover:bg-[#2c323b]'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#64748B] dark:text-gray-400">{tile.label}</span>
                <tile.icon className={`w-4 h-4 ${tile.cls}`} />
              </div>
              <div className="text-[26px] font-bold text-[#0B1220] dark:text-gray-100 mt-1 tabular-nums">{tile.value}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-5 items-start">
        {/* ---------------- Candidate list ---------------- */}
        <aside className={`${cardCls} overflow-hidden`}>
          <div className="p-4 border-b border-[#E3E9F3] dark:border-gray-700 space-y-3">
            {/* Active / History. A paused application stays in Active on
                purpose — you mean to come back to it, and filing it under
                history is how it gets forgotten. Only archived applications
                move. */}
            <div className="flex gap-1 -mx-1" role="tablist">
              {[
                { id: 'active', label: 'Active', count: viewCounts.active },
                { id: 'history', label: 'History', count: viewCounts.history },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={view === tab.id}
                  onClick={() => { setView(tab.id); setStageFilter('all'); }}
                  className={`px-3 py-2 text-[13.5px] font-semibold border-b-2 -mb-px transition-colors ${
                    view === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-[#64748B] dark:text-gray-400 hover:text-[#0B1220] dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                  <span className="ml-1.5 text-[12px] font-medium text-[#94A3B8] dark:text-gray-500 tabular-nums">
                    ({tab.count})
                  </span>
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search candidates..."
                className={`${inputCls} pl-9`}
              />
            </div>
            {/* No stage filter on History: there is nothing to triage in a
                closed application, and "All active stages" would be a lie. */}
            {view === 'active' && (
              <Select
                value={stageFilter}
                onChange={setStageFilter}
                options={filters}
                className="w-full"
              />
            )}
          </div>

          <div className="max-h-[620px] overflow-y-auto">
            {loading ? (
              // Row-shaped shimmer, so re-filtering doesn't collapse the list's
              // height and jump the page.
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 p-4 border-b border-[#F1F5F9] dark:border-gray-800">
                  <Shimmer className="w-9 h-9 rounded-full shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <Shimmer className="h-3.5 w-32 rounded-md" />
                    <Shimmer className="h-3 w-20 rounded-md" />
                    <Shimmer className="h-5 w-24 rounded-full" />
                    <div className="flex gap-3 pt-0.5">
                      <Shimmer className="h-3 w-9 rounded-md" />
                      <Shimmer className="h-3 w-9 rounded-md" />
                      <Shimmer className="h-3 w-9 rounded-md" />
                    </div>
                  </div>
                </div>
              ))
            ) : candidates.length === 0 ? (
              <div className="p-8 text-center">
                <UserPlus className="w-8 h-8 mx-auto text-[#CBD5E1] dark:text-gray-600" />
                <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mt-3">
                  {search || stageFilter !== 'all'
                    ? 'No candidates match your filters.'
                    : view === 'history'
                      ? 'Nothing here yet. Applications you archive, and people you transfer to staff, are kept in History.'
                      : 'No candidates yet. Add one to get started.'}
                </p>
              </div>
            ) : candidates.map((candidate) => (
              <div key={candidate.id} className="relative">
              {/* Always open on step 1 — the earliest step is where an
                  unfamiliar candidate is read from, and 'overview' stopped
                  existing when the tabs became four numbered steps. */}
              <button
                onClick={() => { setActiveId(candidate.id); setActiveStep('pre-interview'); }}
                className={`w-full text-left p-4 border-b border-[#F1F5F9] dark:border-gray-800 transition-colors ${
                  candidate.id === activeId
                    ? 'bg-[#EFF6FF] dark:bg-blue-900/15 border-l-[3px] border-l-blue-600'
                    : 'hover:bg-[#F8FAFC] dark:hover:bg-[#2c323b]'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* The photo they sent with their personal details, where
                      they sent one. Initials stay the fallback, unchanged. */}
                  <UserPhoto
                    src={candidate.avatar_url}
                    alt={candidate.name}
                    className="w-9 h-9 rounded-full"
                    fallback={(
                      <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex items-center justify-center text-[12.5px] font-bold shrink-0">
                        {candidate.initials || '?'}
                      </div>
                    )}
                  />
                  {/* pr-6 reserves the ⋮ button's corner. It sits outside this
                      button element, because a button inside a button is not
                      something a browser will render. */}
                  <div className="min-w-0 flex-1 pr-6">
                    {/* The name WRAPS rather than truncating. A double-barrelled
                        name cut off at "Sameer Alexander Wil…" is the one thing
                        the row exists to tell you, and there is room for a
                        second line in a list this narrow. break-words catches
                        the pathological case — one unbroken 40-character
                        string — so nothing can push the row wider than the
                        column. */}
                    <span className="block text-[14px] font-semibold text-[#0B1220] dark:text-gray-100 break-words">
                      {candidate.name}
                    </span>
                    {/* What they are being recruited FOR. Never role_name —
                        that is the placeholder row every candidate carries until
                        the transfer decides their real role, so it reads the
                        same for all of them and tells nobody anything. */}
                    <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 break-words mt-0.5">
                      {candidate.job_title || '—'}
                    </div>
                    {/* On its own line, under the name. Beside it, the pill was
                        competing with a name of unknown length for the same
                        row — one of the two always lost. */}
                    <div className="mt-2">
                      <StagePill stage={candidate.stage} />
                    </div>
                    {/* History: when it ended. A transferred hire has no
                        candidate progress left to count, so the date is all. */}
                    {view === 'history' && (
                      <div className="flex items-center gap-1 mt-2 text-[11.5px] font-medium text-[#64748B] dark:text-gray-400 tabular-nums">
                        <CalendarClock className="w-3 h-3" />
                        {candidate.transferred
                          ? (candidate.closed_on ? `Transferred ${fmtDate(candidate.closed_on)}` : 'Transfer date not recorded')
                          : (candidate.closed_on ? `Ended ${fmtDate(candidate.closed_on)}` : 'End date not recorded')}
                      </div>
                    )}
                    {!candidate.transferred && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11.5px] text-[#94A3B8] dark:text-gray-500 tabular-nums">
                      <span title="Documents uploaded">
                        <FileText className="w-3 h-3 inline mr-1" />{candidate.documents_uploaded}/{candidate.documents_total}
                      </span>
                      <span title="Manager checks reviewed">
                        <ShieldCheck className="w-3 h-3 inline mr-1" />{candidate.checks_done}/{candidate.checks_total}
                      </span>
                      {/* Only ever shown for somebody a manager asked something
                          extra of, which is a small minority. */}
                      {candidate.extra_documents_outstanding > 0 && (
                        <span title="Requested documents still to come" className="text-amber-600 dark:text-amber-400">
                          <ClipboardCheck className="w-3 h-3 inline mr-1" />{candidate.extra_documents_outstanding} awaited
                        </span>
                      )}
                    </div>
                    )}
                  </div>
                </div>
              </button>

              {/* Row actions. Archiving belongs here rather than only on the
                  open candidate: a manager clearing down a list works through
                  it from the list, and having to open each one first is the
                  reason applications sit in Active for months. A transferred
                  hire is staff now — nothing here applies to them. */}
              {!candidate.transferred && (
              <button
                type="button"
                aria-label={`Actions for ${candidate.name}`}
                title="Actions"
                onClick={(event) => { event.stopPropagation(); setMenuFor(menuFor === candidate.id ? null : candidate.id); }}
                className="absolute top-3 right-2 p-1.5 rounded-lg text-[#94A3B8] hover:text-[#334155] hover:bg-white dark:hover:bg-[#31373f] dark:hover:text-gray-200 transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              )}

              {menuFor === candidate.id && (
                <>
                  {/* Clicking anywhere else closes the menu. A transparent
                      full-screen layer is the only way to catch that without
                      a document listener that outlives the component. */}
                  <div className="fixed inset-0 z-20" onClick={() => setMenuFor(null)} />
                  <div className="absolute top-9 right-2 z-30 w-48 py-1 rounded-xl border border-[#E3E9F3] dark:border-gray-700 bg-white dark:bg-[#262b33] shadow-lg">
                    {ARCHIVE_REASONS.includes(candidate.stage) ? (
                      <button
                        type="button"
                        onClick={() => openReopen(candidate)}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-[13px] text-[#1E293B] dark:text-gray-200 hover:bg-[#F8FAFC] dark:hover:bg-[#2c323b]"
                      >
                        <RotateCcw className="w-4 h-4 text-[#64748B]" />
                        Reopen application
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openArchive(candidate)}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-[13px] text-[#1E293B] dark:text-gray-200 hover:bg-[#F8FAFC] dark:hover:bg-[#2c323b]"
                      >
                        <Archive className="w-4 h-4 text-[#64748B]" />
                        Archive candidate
                      </button>
                    )}
                  </div>
                </>
              )}
              </div>
            ))}
          </div>
        </aside>

        {/* ---------------- Candidate detail ---------------- */}
        {loadingActive ? (
          <div className="space-y-5">
            <RecruitmentPanelSkeleton rows={2} />
            <RecruitmentPanelSkeleton rows={3} />
          </div>
        ) : (
        <section className={`${cardCls} overflow-hidden min-h-[400px]`}>
          {!active ? (
            <div className="p-16">
              <div className="text-center">
                <Info className="w-8 h-8 mx-auto text-[#CBD5E1] dark:text-gray-600" />
                <p className="text-[13.5px] text-[#64748B] dark:text-gray-400 mt-3">Select a candidate to see their details.</p>
              </div>
            </div>
          ) : active.transferred ? (
            <TransferredRecord candidate={active} onOpenStaff={(id) => navigate(`/manager/staffs/${id}/view`)} />
          ) : (
            <>
              {/* Header */}
              <div className="p-5 border-b border-[#E3E9F3] dark:border-gray-700">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <UserPhoto
                      src={active.avatar_url}
                      alt={active.name}
                      className="w-12 h-12 rounded-full"
                      fallback={(
                        <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex items-center justify-center text-[15px] font-bold shrink-0">
                          {active.initials || '?'}
                        </div>
                      )}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h2 className="text-[19px] font-bold text-[#0B1220] dark:text-gray-100 break-words min-w-0">{active.name}</h2>
                        <StagePill stage={active.stage} />
                      </div>
                      <div className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">
                        {active.job_title ? `${active.job_title} · ` : ''}
                        Applied {fmtDate(active.applied_at) || '—'}
                        {active.source ? ` · via ${active.source}` : ''}
                      </div>
                      <div className="text-[13px] text-[#64748B] dark:text-gray-400">
                        {active.email}{active.phone ? ` · ${active.phone}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* A shortcut to step 4, not a second way of doing it —
                        the roster and access still have to be set there. */}
                    {!locked && (
                      <Button
                        variant="success" size="sm" leftIcon={<UserCheck className="w-4 h-4" />}
                        disabled={active.stage !== 'Ready to transfer'}
                        title={active.stage === 'Ready to transfer'
                          ? 'Set their roster and access, and create the staff record'
                          : 'Not everything is complete yet — see what\'s outstanding below'}
                        onClick={() => setActiveStep('transfer')}
                      >
                        Transfer to staff
                      </Button>
                    )}
                    {archived ? (
                      // The archived banner below carries its own Reopen, so
                      // the header does not repeat it.
                      null
                    ) : locked ? (
                      <Button
                        variant="secondary" size="sm" leftIcon={<RotateCcw className="w-4 h-4" />} loading={busy}
                        onClick={() => run(() => recruitmentService.setStage(active.id, 'Resume')).then(loadCandidates)}
                      >
                        Take off hold
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="secondary" size="sm" leftIcon={<Ban className="w-4 h-4" />}
                          title="Pause this application without ending it"
                          onClick={() => run(() => recruitmentService.setStage(active.id, 'On hold')).then(loadCandidates)}
                        >
                          Put on hold
                        </Button>
                        <Button variant="secondary" size="sm" leftIcon={<Archive className="w-4 h-4" />} onClick={() => openArchive(active)}>
                          Archive
                        </Button>
                      </>
                    )}
                    <Button
                      variant="danger-soft" size="sm" icon={<Trash2 className="w-4 h-4" />}
                      aria-label="Delete candidate" onClick={() => handleDelete(active)}
                    />
                  </div>
                </div>

                {/* An archived application says so, plainly and at the top.
                    Everything below stays readable — the record is the point of
                    keeping it — but nothing about it is still in progress, and a
                    screen that looked identical to an open application is how
                    somebody ends up chasing a candidate who was turned down. */}
                {archived && (
                  <div className="mt-4 p-4 rounded-xl border border-[#E3E9F3] dark:border-gray-700 bg-[#F8FAFC] dark:bg-[#2c323b]">
                    <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">Archived application</div>
                    <div className="text-[13px] text-[#475569] dark:text-gray-300 mt-1.5">
                      <span className="font-semibold">{active.archive.reason}</span>
                      {' · '}
                      {/* An application archived by an interview decision was
                          never given a date. Saying so is better than printing
                          the day somebody happened to open this screen. */}
                      {active.archive.date
                        ? `Archived ${fmtDate(active.archive.date)}`
                        : active.archive.interview_date
                          // Ended by the interview outcome: its date is the interview's.
                          ? `Interview outcome · interviewed ${fmtDate(active.archive.interview_date)}`
                          : 'Archive date not recorded'}
                      {active.archive.archived_by ? ` · by ${active.archive.archived_by}` : ''}
                    </div>
                    {active.archive.note && (
                      <p className="text-[13px] text-[#475569] dark:text-gray-300 mt-2 whitespace-pre-wrap">{active.archive.note}</p>
                    )}
                    <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">
                      Read-only record. Documents and interview history are retained; candidate forms are closed.
                    </p>
                    <div className="mt-3">
                      <Button variant="secondary" size="sm" leftIcon={<RotateCcw className="w-4 h-4" />} onClick={() => openReopen(active)}>
                        Reopen application
                      </Button>
                    </div>
                  </div>
                )}

                {/* Where every form has got to. Without this the transfer button
                    is simply greyed out and a single unticked requirement out of
                    seventeen is impossible to spot.

                    It stays on screen once the candidate is ready, because the
                    references row is not a gate and an outstanding reference is
                    most worth seeing at exactly the moment somebody is about to
                    transfer them. */}
                {!locked && (active.readiness || []).some((r) => !r.met) && (
                  <div className="mt-4 p-4 rounded-xl border border-[#E3E9F3] dark:border-gray-700 bg-[#F8FAFC] dark:bg-[#2c323b]">
                    <div className="text-[11.5px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500 mb-2.5">
                      {active.stage === 'Ready to transfer' ? 'Still outstanding' : 'Outstanding before transfer'}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {(active.readiness || []).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setActiveStep(item.step)}
                          className={`flex items-start gap-2 text-left text-[13px] py-0.5 rounded ${
                            item.met
                              ? 'text-[#94A3B8] dark:text-gray-500'
                              : 'text-[#1E293B] dark:text-gray-200 hover:text-blue-600'
                          }`}
                        >
                          {item.met
                            ? <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-px" />
                            : (
                              <span className={`w-4 h-4 shrink-0 mt-px rounded-full border-2 ${
                                item.blocking === false ? 'border-[#CBD5E1] dark:border-gray-600' : 'border-amber-500'
                              }`}
                              />
                            )}
                          <span className="min-w-0">
                            {item.label}
                            {/* A row that does not hold up the transfer says so.
                                A reference is somebody else's homework and can
                                take weeks — listing it beside things the manager
                                can actually fix, with nothing to distinguish it,
                                would read as a blocker it is not. */}
                            {item.blocking === false && !item.met && (
                              <span className="ml-1.5 text-[11.5px] font-semibold text-[#94A3B8] dark:text-gray-500">
                                (does not block)
                              </span>
                            )}
                            {item.outstanding.length > 0 && (
                              <span className="block text-[12px] text-[#64748B] dark:text-gray-400">
                                {item.outstanding.join(', ')}
                              </span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Next-action prompt — one clear instruction, always. */}
                {nextAction && (
                  <div className={`mt-4 flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border ${
                    nextAction.action === 'convert'
                      ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/40'
                      : 'bg-[#F8FAFC] dark:bg-[#2c323b] border-[#E3E9F3] dark:border-gray-700'
                  }`}>
                    <div className="min-w-0">
                      <div className="text-[11.5px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">{nextAction.eyebrow}</div>
                      <div className="text-[14px] font-medium text-[#1E293B] dark:text-gray-200 mt-0.5">{nextAction.title}</div>
                    </div>
                    {nextAction.action === 'convert' ? (
                      <Button variant="success" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />} onClick={() => setActiveStep('transfer')}>
                        Transfer to staff
                      </Button>
                    ) : nextAction.action === 'invite' ? (
                      <Button variant="primary" size="sm" leftIcon={<Send className="w-4 h-4" />} onClick={() => setShowInvite(true)}>
                        Send request
                      </Button>
                    ) : nextAction.action === 'reopen' ? null : (
                      <Button variant="secondary" size="sm" onClick={() => setActiveStep(nextAction.step)}>
                        {nextAction.label}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* The four steps.
                  Numbered and marked complete rather than merely selected: the
                  manager needs to see how far this candidate has got, not just
                  which panel is open. Completion comes from the candidate's
                  derived stage — a step is done once they have moved past it. */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 px-5 py-4 border-b border-[#E3E9F3] dark:border-gray-700 bg-[#FBFCFE] dark:bg-[#22262d]">
                {STEPS.map((step, index) => {
                  const isActive = activeStep === step.id;
                  // Recorded when the step was finished — the day the candidate
                  // returned that form, or the day of the interview. A step can
                  // be complete with no date (a manager who attached the
                  // documents themselves), which shows as a plain "Completed".
                  const completedOn = fmtDate(active.step_dates?.[step.id]);
                  // Where the candidate actually is. A paused application keeps
                  // whatever progress it had, so fall back to the step in view.
                  const reached = STEPS.findIndex((row) => row.stage === active.stage);
                  const currentIndex = reached === -1 ? 0 : reached;
                  const isComplete = index < currentIndex
                    || (step.id === 'transfer' && active.stage === 'Ready to transfer');

                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => setActiveStep(step.id)}
                      aria-current={isActive ? 'step' : undefined}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                        isActive
                          ? 'border-blue-600 bg-[#EEF3FF] dark:bg-blue-900/20'
                          : isComplete
                            ? 'border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/10 hover:border-emerald-300'
                            : 'border-[#E3E9F3] dark:border-gray-700 hover:border-[#CBD5E1]'
                      }`}
                    >
                      <span className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[12.5px] font-bold border ${
                        isComplete
                          ? 'bg-emerald-600 border-emerald-600 text-white'
                          : isActive
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'border-[#CBD5E1] dark:border-gray-600 text-[#64748B] dark:text-gray-400'
                      }`}>
                        {isComplete ? <Check className="w-3.5 h-3.5" /> : index + 1}
                      </span>
                      <span className="min-w-0">
                        <span className={`block text-[13px] font-semibold truncate ${
                          isActive ? 'text-blue-700 dark:text-blue-300' : 'text-[#0B1220] dark:text-gray-100'
                        }`}>
                          {step.label}
                        </span>
                        {/* The date wins over "Viewing" when there is one: the
                            blue border already says which panel is open, and
                            when this step was finished is the thing a manager
                            cannot get anywhere else on the row. */}
                        <span className="block text-[11.5px] text-[#94A3B8] dark:text-gray-500 truncate">
                          {isComplete || completedOn
                            ? `Completed${completedOn ? ` · ${completedOn}` : ''}`
                            : isActive ? 'Viewing' : `Step ${index + 1}`}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="p-5 space-y-5">
                {/* The same fact at the top of the panel the manager is reading.
                    The step header above is a row of four small tiles that is
                    easy to scan past; this is the one they are actually looking
                    at, and it answers "is this done, and when" before they read
                    anything in it. */}
                {fmtDate(active.step_dates?.[activeStep]) && (
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/10">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="text-[13px] text-[#1E293B] dark:text-gray-200">
                      This step was completed on{' '}
                      <strong className="font-semibold">{fmtDate(active.step_dates[activeStep])}</strong>
                    </span>
                  </div>
                )}

                {/* ============ Step 3 · Personal details ============ */}
                {activeStep === 'personal' && (
                  <PersonalDetailsPanel
                    candidate={active}
                    definitions={definitions}
                    busy={busy}
                    locked={locked}
                    run={run}
                    onRefreshList={loadCandidates}
                    onViewAnswers={() => setShowSubmission(true)}
                  />
                )}

                {/* ============ Step 2 · Interview ============ */}
                {activeStep === 'interview' && (
                  <div className="space-y-5">
                  <div className={`${cardCls} p-5`}>
                    <CardHead
                      title="Interview details"
                      subtitle="Record the interview, assessment and decision."
                      pill={(
                        <CardPill tone={active.interview_decision === 'Successful' ? 'good' : active.interview_decision === 'Pending' ? 'neutral' : 'warn'}>
                          {active.interview_decision === 'Pending' ? 'Not recorded' : 'Outcome recorded'}
                        </CardPill>
                      )}
                    />

                    {/* Where the applicant came from, captured on Add Candidate.
                        Read-only: it is not something the panel decides, but it
                        is something they ask about in the room. One line under
                        the heading rather than a grid cell — it is context, not
                        a field to fill in. */}
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 -mt-3 mb-5">
                      <span className="text-[12.5px] text-[#64748B] dark:text-gray-400">Application source:</span>
                      <span className="text-[12.5px] font-semibold text-[#0B1220] dark:text-gray-100">
                        {active.source || 'Not recorded'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                      <div>
                        <label className={fieldLabelCls} htmlFor="interview-date">Interview date</label>
                        <input
                          id="interview-date"
                          type="date" className={inputCls} disabled={locked}
                          value={interviewForm.interview_date}
                          onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_date: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className={fieldLabelCls} htmlFor="interview-time">Interview time</label>
                        <input
                          id="interview-time"
                          type="time" className={inputCls} disabled={locked}
                          value={interviewForm.interview_time}
                          onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_time: e.target.value }))}
                        />
                      </div>

                      {/* How it happens — and so what the invitation email has
                          to carry: an address, or a link to join. */}
                      <div className="md:col-span-2">
                        <span className={fieldLabelCls} id="interview-format-label">Interview format</span>
                        <div className="inline-flex p-1 rounded-xl bg-[#F1F5F9] dark:bg-[#1f2329]" role="radiogroup" aria-labelledby="interview-format-label">
                          {INTERVIEW_MODES.map(({ value, icon: ModeIcon }) => {
                            const on = interviewForm.interview_mode === value;
                            return (
                              <button
                                key={value} type="button" role="radio" aria-checked={on} disabled={locked}
                                onClick={() => setInterviewForm((prev) => ({ ...prev, interview_mode: value }))}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13.5px] font-semibold transition disabled:cursor-not-allowed ${on
                                  ? 'bg-white dark:bg-[#31373f] text-[#2563EB] dark:text-blue-300 shadow-sm'
                                  : 'text-[#64748B] dark:text-gray-400 hover:text-[#0B1220] dark:hover:text-gray-200'}`}
                              >
                                <ModeIcon className="w-4 h-4" /> {value}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {interviewForm.interview_mode === 'In person' && (
                        <div className="md:col-span-2">
                          <label className={fieldLabelCls} htmlFor="interview-address">Address</label>
                          <textarea
                            id="interview-address" rows="2" className={`${inputCls} resize-y`} disabled={locked}
                            placeholder="Full address, including the postcode"
                            value={interviewForm.interview_address}
                            onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_address: e.target.value }))}
                          />
                        </div>
                      )}

                      {interviewForm.interview_mode === 'Online' && (
                        <div className="md:col-span-2">
                          <label className={fieldLabelCls} htmlFor="interview-link">Meeting link</label>
                          <input
                            id="interview-link" type="url" inputMode="url" className={inputCls} disabled={locked}
                            placeholder="https://meet.google.com/… or a Teams / Zoom link"
                            value={interviewForm.interview_link}
                            onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_link: e.target.value }))}
                          />
                          {interviewForm.interview_link.trim() && !isMeetingLink(interviewForm.interview_link) ? (
                            <p className="text-[12px] text-rose-600 dark:text-rose-400 mt-1.5">Paste the full link, starting https://</p>
                          ) : (
                            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">The invitation&rsquo;s &ldquo;Join the interview&rdquo; button opens this link.</p>
                          )}
                        </div>
                      )}

                      {interviewForm.interview_mode && (
                        <div className="md:col-span-2">
                          <label className={fieldLabelCls} htmlFor="interview-instructions">
                            Instructions for the candidate <span className="font-medium normal-case text-[#94A3B8]">(optional)</span>
                          </label>
                          <textarea
                            id="interview-instructions" rows="2" className={`${inputCls} resize-y`} disabled={locked}
                            placeholder={interviewForm.interview_mode === 'Online'
                              ? 'e.g. Join 5 minutes early with your camera on, and have photo ID ready'
                              : 'e.g. Park at the rear, ask for Jo at reception, and bring photo ID'}
                            value={interviewForm.interview_instructions}
                            onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_instructions: e.target.value }))}
                          />
                          <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">Included in the invitation email.</p>
                        </div>
                      )}

                      {/* What the recruiter wrote when they added this candidate.
                          Those notes are written FOR this moment — "available
                          immediately, ask about the gap in 2024" is a note to the
                          interviewer — and they were sitting on a screen the
                          interviewer had no reason to open.

                          Shown, not prefilled into the panel's own notes: they
                          are somebody else's words, and a box that silently
                          starts as the recruiter's text gets saved back as the
                          panel's findings. Read-only for the same reason. */}
                      {active.details?.recruitment_notes && (
                        <div className="md:col-span-2 px-4 py-3.5 rounded-xl bg-[#F4F8FF] dark:bg-[#20242c] border border-[#E3EBF6] dark:border-gray-800">
                          <div className={`${fieldLabelCls} mb-1.5`}>Notes for the interview</div>
                          <p className="text-[13.5px] text-[#1E293B] dark:text-gray-200 whitespace-pre-wrap">
                            {active.details.recruitment_notes}
                          </p>
                          <p className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
                            Recruiter notes from Add Candidate
                          </p>
                        </div>
                      )}

                      <div className="md:col-span-2">
                        <label className={fieldLabelCls}>Interview panel</label>
                        <InterviewPanelPicker
                          value={interviewForm.interview_panel}
                          staff={staffList}
                          disabled={locked}
                          onChange={(names) => setInterviewForm((prev) => ({ ...prev, interview_panel: names }))}
                        />
                      </div>

                      <div>
                        <label className={fieldLabelCls} htmlFor="interview-assessment">Assessment summary</label>
                        <input
                          id="interview-assessment"
                          className={inputCls} disabled={locked} placeholder="Summarise the assessment"
                          value={interviewForm.interview_assessment}
                          onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_assessment: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className={fieldLabelCls} htmlFor="interview-score">
                          Score <span className="font-medium normal-case text-[#94A3B8]">(optional)</span>
                        </label>
                        <input
                          id="interview-score"
                          className={inputCls} disabled={locked} placeholder="Enter score"
                          value={interviewForm.interview_score}
                          onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_score: e.target.value }))}
                        />
                        {/* Free text on purpose: homes score out of five, out of
                            ten, or in words, and a number box would force a
                            scale nobody agreed to. */}
                        <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
                          Use your interview scoring scale.
                        </p>
                      </div>

                      <div className="md:col-span-2">
                        <label className={fieldLabelCls}>Interview outcome</label>
                        <Select
                          value={interviewForm.interview_decision}
                          onChange={(v) => setInterviewForm((prev) => ({ ...prev, interview_decision: v }))}
                          options={OUTCOME_OPTIONS}
                          className="w-full"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className={fieldLabelCls}>Panel notes and rationale</label>
                        <textarea
                          rows="5" className={`${inputCls} resize-none font-medium`} disabled={locked}
                          placeholder="Record interview findings and reasons for the decision"
                          value={interviewForm.interview_outcome}
                          onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_outcome: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-5 border-t border-[#F1F5F9] dark:border-gray-800">
                      {/* Shortened to the one thing this line has to prevent: a
                          manager reading a successful outcome as "the checks
                          passed". The longer version said it twice. */}
                      <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 flex items-start gap-2 max-w-2xl">
                        <ShieldCheck className="w-4 h-4 shrink-0 mt-px text-[#94A3B8]" />
                        Record outstanding checks separately.
                      </p>
                      <Button variant="primary" loading={busy} disabled={locked} onClick={() => handleSaveInterview()}>
                        Save interview
                      </Button>
                    </div>
                  </div>

                  {renderInterviewEmails()}
                  </div>
                )}

                {/* ============ Step 1 · Pre-interview ============ */}
                {activeStep === 'pre-interview' && (
                  <PreInterviewPanel
                    candidate={active}
                    definitions={definitions}
                    busy={busy}
                    locked={locked}
                    run={run}
                    onRefreshList={loadCandidates}
                  />
                )}

                {/* ============ Step 4 · Transfer to staff ============ */}
                {activeStep === 'transfer' && (
                  <TransferPanel
                    candidate={active}
                    definitions={definitions}
                    busy={busy}
                    locked={locked}
                    run={run}
                    availableMenus={availableMenus}
                    loadingMenus={loadingMenus}
                    domiciliary={isDomiciliaryOrg}
                    groups={groups}
                    onTransferred={(staff) => navigate(`/manager/staffs/${staff.id}/view`)}
                  />
                )}
              </div>
            </>
          )}
        </section>
        )}
      </div>

      {/* ---------------- Add Candidate ---------------- */}
      <Modal
        isOpen={showAdd} onClose={() => setShowAdd(false)} title="Add Candidate" maxWidth="max-w-2xl"
        footer={(
          <div className="flex items-center justify-between gap-2.5">
            <Button variant="ghost" size="sm" leftIcon={<Wand2 className="w-4 h-4" />} onClick={fillTestCandidate}>
              Fill test data
            </Button>
            <div className="flex gap-2.5">
              <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button
              variant="primary" loading={busy} onClick={handleAdd}
              disabled={emailCheck.state === 'taken'}
            >
              Create Candidate
            </Button>
            </div>
          </div>
        )}
      >
        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-5">
          Just enough to identify the applicant and email them their form. They fill in the rest themselves — you don't set a user ID or PIN until they're ready to join.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
          <div>
            <label className={fieldLabelCls}>Full name <span className="text-rose-500">*</span></label>
            <input
              className={inputCls} value={addForm.name} placeholder="Candidate's full name"
              onChange={(e) => setAddForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div>
            <label className={fieldLabelCls}>Email address <span className="text-rose-500">*</span></label>
            <input
              type="email"
              className={`${inputCls} ${emailCheck.state === 'taken' ? 'border-rose-400 dark:border-rose-500' : ''}`}
              value={addForm.email}
              placeholder="candidate@example.com"
              onChange={(e) => setAddForm((prev) => ({ ...prev, email: e.target.value }))}
            />
            {emailCheck.state === 'checking' && (
              <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">Checking…</p>
            )}
            {emailCheck.state === 'taken' && (
              <p className="text-[12px] font-semibold text-rose-600 dark:text-rose-400 mt-1.5">{emailCheck.message}</p>
            )}
          </div>
          <div>
            <label className={fieldLabelCls}>Telephone</label>
            <input
              className={inputCls} value={addForm.phone} placeholder="+44 7700 900000"
              onChange={(e) => setAddForm((prev) => ({ ...prev, phone: e.target.value }))}
            />
          </div>
          <div>
            <label className={fieldLabelCls}>Application source</label>
            <Select
              value={addForm.source} onChange={(v) => setAddForm((prev) => ({ ...prev, source: v }))}
              options={SOURCE_OPTIONS} className="w-full"
            />
          </div>
          <div>
            {/* What they applied FOR. The same column and the same suggestions
                as Employment details on Add & Edit Staff, so the title the
                candidate was recruited under is already filled in when the
                record lands there at transfer. Suggestions, not a closed list. */}
            <label className={fieldLabelCls}>Job title</label>
            <input
              className={inputCls} value={addForm.job_title} list="candidateJobTitleOptions"
              placeholder="Type or choose a job title"
              onChange={(e) => setAddForm((prev) => ({ ...prev, job_title: e.target.value }))}
            />
            <datalist id="candidateJobTitleOptions">
              {JOB_TITLES.map((title) => <option key={title} value={title} />)}
            </datalist>
          </div>
          <div>
            <label className={fieldLabelCls}>Interview date</label>
            <input
              type="date" className={inputCls} value={addForm.interview_date}
              onChange={(e) => setAddForm((prev) => ({ ...prev, interview_date: e.target.value }))}
            />
          </div>
          <div className="md:col-span-2">
            <label className={fieldLabelCls}>Recruiter notes</label>
            <textarea
              rows="3" className={`${inputCls} resize-none`} value={addForm.notes}
              placeholder="Availability, experience or points to cover at interview"
              onChange={(e) => setAddForm((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </div>
        </div>
      </Modal>

      {/* ---------------- Send information request ---------------- */}
      <Modal
        isOpen={showInvite} onClose={() => setShowInvite(false)} title="Send candidate information request" maxWidth="max-w-xl"
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button variant="primary" loading={busy} leftIcon={<Send className="w-4 h-4" />} onClick={handleSendLink}>
              Send request
            </Button>
          </div>
        )}
      >
        <div className="p-4 rounded-xl bg-[#EEF3FF] dark:bg-blue-900/15 border border-[rgba(37,99,235,0.14)] mb-5">
          <div className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">Basic information requested first</div>
          <p className="text-[12.5px] text-[#475569] dark:text-gray-300 mt-1">
            Personal and contact details, emergency contact, employment history, payroll details and the initial supporting documents.
            DBS and Right to Work provider links are sent later from the Checks tab. The candidate is never asked for a user ID or PIN.
          </p>
        </div>
        <div className="space-y-4">
          <div>
            <label className={fieldLabelCls}>Sending to</label>
            <input className={`${inputCls} bg-[#F8FAFC] dark:bg-[#31373f]`} value={active?.email || ''} readOnly />
          </div>
          <div>
            <label className={fieldLabelCls}>Link expires after</label>
            <Select
              value={inviteForm.expiry_days}
              onChange={(v) => setInviteForm((prev) => ({ ...prev, expiry_days: v }))}
              options={[
                { value: '7', label: '7 days' },
                { value: '14', label: '14 days' },
                { value: '30', label: '30 days' },
              ]}
              className="w-full"
            />
          </div>
          <div>
            <label className={fieldLabelCls}>Message</label>
            <textarea
              rows="3" className={`${inputCls} resize-none`} value={inviteForm.message}
              placeholder="Optional note — leave blank to use the standard wording"
              onChange={(e) => setInviteForm((prev) => ({ ...prev, message: e.target.value }))}
            />
          </div>
        </div>
      </Modal>

      {/* ---------------- Link result ---------------- */}
      <Modal
        isOpen={!!linkResult} onClose={() => setLinkResult(null)} title="Candidate form link" maxWidth="max-w-xl"
        footer={<div className="flex justify-end"><Button variant="primary" onClick={() => setLinkResult(null)}>Done</Button></div>}
      >
        <div className={`p-4 rounded-xl mb-4 border flex items-start gap-3 ${
          linkResult?.emailed
            ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/40'
            : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/40'
        }`}>
          {linkResult?.emailed
            ? <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            : <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
          <div className="text-[13px] text-[#475569] dark:text-gray-300">
            {linkResult?.emailed
              ? `Emailed to ${active?.email}. You can also share the link directly.`
              : 'The email could not be sent, so share this link with the candidate directly.'}
          </div>
        </div>
        <label className={fieldLabelCls}>Secure link (expires {fmtDate(linkResult?.expires_at)})</label>
        <div className="flex items-center gap-2">
          <input className={`${inputCls} font-mono text-[12px]`} value={linkResult?.url || ''} readOnly />
          <Button variant="secondary" icon={<Copy className="w-4 h-4" />} aria-label="Copy link" onClick={() => copyLink(linkResult.url)} />
          <a href={linkResult?.url} target="_blank" rel="noreferrer">
            <Button variant="secondary" icon={<ExternalLink className="w-4 h-4" />} aria-label="Open link" />
          </a>
        </div>
      </Modal>

      {/* ---------------- Interview invitation / outcome email ---------------- */}
      <Modal
        isOpen={Boolean(emailDialog && active)} onClose={() => setEmailDialog(null)} maxWidth="max-w-xl"
        title={(() => {
          if (!emailDialog || !active) return '';
          if (emailDialog.type === 'invitation') {
            return active.interview_emails?.invitation?.changed ? 'Send updated interview invitation' : 'Send interview invitation';
          }
          const decision = active.interview_emails?.outcome?.decision;
          return decision ? `Send ${OUTCOME_EMAIL[decision].label.toLowerCase()}` : 'Send outcome email';
        })()}
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setEmailDialog(null)}>Not now</Button>
            <Button variant="primary" loading={busy} leftIcon={<Send className="w-4 h-4" />} onClick={handleSendInterviewEmail}>
              Send email
            </Button>
          </div>
        )}
      >
        {emailDialog && active && (() => {
          const d = active.details || {};
          const emails = active.interview_emails || {};
          const isInvite = emailDialog.type === 'invitation';
          const decision = emails.outcome?.decision;
          const previous = isInvite ? emails.invitation?.sent : emails.outcome?.sent;
          const online = d.interview_mode === 'Online';
          return (
            <div className="space-y-4">
              {isInvite ? (
                <div className="px-4 rounded-xl border border-[#E3E9F3] dark:border-gray-700">
                  <SummaryRow label="To" value={active.email} />
                  <SummaryRow label="Date" value={longDate(d.interview_date)} />
                  <SummaryRow label="Time" value={shortTime(d.interview_time)} />
                  <SummaryRow label="Format" value={online ? 'Online (video call)' : 'In person'} />
                  {online
                    ? <SummaryRow label="Meeting link" value={<span className="break-all font-mono text-[12px]">{d.interview_link}</span>} />
                    : <SummaryRow label="Address" value={<span className="whitespace-pre-wrap">{d.interview_address}</span>} />}
                  {d.interview_instructions && <SummaryRow label="Instructions" value={<span className="whitespace-pre-wrap font-medium">{d.interview_instructions}</span>} />}
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-[#EEF3FF] dark:bg-blue-900/15 border border-[rgba(37,99,235,0.14)]">
                  <div className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">
                    {decision ? OUTCOME_EMAIL[decision].label : 'Outcome email'} to {active.email}
                  </div>
                  <p className="text-[12.5px] text-[#475569] dark:text-gray-300 mt-1">{decision && OUTCOME_EMAIL[decision].summary}</p>
                </div>
              )}

              {isInvite && emails.invitation?.changed && (
                <p className="text-[12.5px] text-amber-700 dark:text-amber-400 flex items-start gap-2">
                  <Info className="w-4 h-4 shrink-0 mt-px" />
                  Goes as an update — the subject tells them the details have changed since {fmtDateTime(previous?.sent_at)}.
                </p>
              )}
              {previous && !(isInvite && emails.invitation?.changed) && (
                <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 flex items-start gap-2">
                  <Info className="w-4 h-4 shrink-0 mt-px" />
                  Already sent on {fmtDateTime(previous.sent_at)} by {previous.by}. This sends it again.
                </p>
              )}

              <div>
                <label className={fieldLabelCls} htmlFor="interview-email-message">
                  Personal note <span className="font-medium normal-case text-[#94A3B8]">(optional)</span>
                </label>
                <textarea
                  id="interview-email-message" rows="3" className={`${inputCls} resize-none`}
                  placeholder={isInvite
                    ? 'Added under the standard wording — e.g. who they will meet'
                    : 'Added under the standard wording'}
                  value={emailDialog.message}
                  onChange={(e) => setEmailDialog((prev) => ({ ...prev, message: e.target.value }))}
                />
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ---------------- Archive candidate ---------------- */}
      <Modal
        isOpen={Boolean(archiveTarget)} onClose={() => setArchiveTarget(null)}
        title="Archive candidate" maxWidth="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setArchiveTarget(null)}>Cancel</Button>
            <Button variant="primary" loading={busy} disabled={!archiveForm.date} onClick={handleArchive}>
              Confirm &amp; archive
            </Button>
          </div>
        )}
      >
        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-5">
          {archiveTarget?.name} will move out of the active list. Their details and documents will be retained.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            {/* The reason becomes their status, which is why it is a fixed list
                rather than free text: "we said no", "they said no" and "it
                stopped elsewhere" are the three things anyone asks of an
                archived application a year later. */}
            <label className={fieldLabelCls}>Reason</label>
            <Select
              value={archiveForm.reason}
              onChange={(value) => setArchiveForm((prev) => ({ ...prev, reason: value }))}
              options={ARCHIVE_REASONS.map((value) => ({ value, label: value }))}
              className="w-full"
            />
          </div>
          <div>
            <label className={fieldLabelCls}>Archive date *</label>
            <input
              type="date" className={inputCls} value={archiveForm.date}
              onChange={(e) => setArchiveForm((prev) => ({ ...prev, date: e.target.value }))}
            />
          </div>
        </div>

        <div className="mt-4">
          <label className={fieldLabelCls}>Notes</label>
          <textarea
            rows="3" className={`${inputCls} resize-none`} value={archiveForm.note}
            placeholder="Anything worth knowing if this record is read again later"
            onChange={(e) => setArchiveForm((prev) => ({ ...prev, note: e.target.value }))}
          />
        </div>
      </Modal>

      {/* ---------------- Reopen application ---------------- */}
      <Modal
        isOpen={Boolean(reopenTarget)} onClose={() => setReopenTarget(null)}
        title="Reopen application" maxWidth="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setReopenTarget(null)}>Cancel</Button>
            <Button variant="primary" loading={busy} disabled={!reopenReason.trim()} onClick={handleReopen}>
              Confirm &amp; reopen
            </Button>
          </div>
        )}
      >
        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-5">
          {reopenTarget?.name} will return to Active.
          {['Unsuccessful', 'Withdrawn'].includes(reopenTarget?.interview_decision)
            ? ' An unsuccessful interview will need a new outcome.'
            : ''}
          {' '}Previous decisions and documents will be retained.
        </p>

        {/* Required, and the API enforces it too. Bringing back somebody who was
            told no is a decision that has to be explainable afterwards. */}
        <label className={fieldLabelCls}>Reason for reopening *</label>
        <textarea
          rows="3" className={`${inputCls} resize-none`} value={reopenReason}
          onChange={(e) => setReopenReason(e.target.value)}
        />
        <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">
          Old candidate links stay closed. Prepare a new request link if needed.
        </p>
      </Modal>

      {/* ---------------- Candidate submission preview ---------------- */}
      <Modal
        isOpen={showSubmission} onClose={() => setShowSubmission(false)}
        title="Candidate form" maxWidth="max-w-3xl"
        footer={(
          <div className="flex items-center justify-between gap-2.5">
            {active?.form_url ? (
              <Button
                variant="ghost" size="sm" leftIcon={<ExternalLink className="w-4 h-4" />}
                onClick={() => window.open(active.form_url, '_blank', 'noopener')}
              >
                Open the live form
              </Button>
            ) : <span />}
            <Button variant="primary" onClick={() => setShowSubmission(false)}>Done</Button>
          </div>
        )}
      >
        <div className={`p-4 rounded-xl mb-5 border flex items-start gap-3 ${
          active?.link_status === 'Submitted'
            ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/40'
            : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/40'
        }`}>
          {active?.link_status === 'Submitted'
            ? <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            : <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
          <div className="text-[13px] text-[#475569] dark:text-gray-300">
            {active?.link_status === 'Submitted'
              ? `Submitted ${fmtDateTime(active.submitted_at)}. These are the candidate's own answers — an uploaded document is not verified evidence until you verify it on the Documents tab.`
              : active?.link_status === 'Not sent'
                ? 'The information request hasn\'t been sent yet, so there is nothing here from the candidate.'
                : `Sent ${fmtDateTime(active?.link_sent_at)} — not submitted yet. Anything the candidate has saved so far is shown below.`}
          </div>
        </div>

        <div className="space-y-5">
          {/* Same groups the candidate sees on their own copy — one shared
              definition, so the two views can't drift apart. */}
          {buildCandidateSummary({
            name: active?.name,
            email: active?.email,
            phone: active?.phone,
            details: active?.details,
          }).map((group) => (
            <div key={group.title} className="border border-[#E3E9F3] dark:border-gray-700 rounded-xl p-4">
              <h3 className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 mb-3.5">{group.title}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5">
                {group.fields.map((field) => (
                  <div key={field.label} className={field.full ? 'sm:col-span-2' : ''}>
                    <div className="text-[11.5px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">
                      {field.label}
                    </div>
                    <div className="text-[13.5px] text-[#1E293B] dark:text-gray-200 mt-0.5 whitespace-pre-wrap break-words">
                      {field.value || '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="border border-[#E3E9F3] dark:border-gray-700 rounded-xl p-4">
            <h3 className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 mb-3.5">Documents</h3>
            {/* Files only. There is deliberately no "verified" badge per
                document: what was decided is the manager check on step 1, and
                repeating a verdict next to each file is how a received
                document starts reading as an approved one. */}
            <div className="space-y-2">
              {(active?.documents || []).map((group) => (
                <div key={group.id} className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-[#E3E9F3] dark:border-gray-700">
                  <FileText className="w-4 h-4 text-[#94A3B8] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-[#1E293B] dark:text-gray-200">{group.label}</div>
                    {group.files.filter((file) => file.file_url).length ? (
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                        {group.files.filter((file) => file.file_url).map((file) => (
                          <a
                            key={file.id}
                            href={fileHref(file.file_url)} target="_blank" rel="noreferrer"
                            className="text-[12px] text-blue-600 hover:underline break-all"
                          >
                            {file.doc_type || file.file_name}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">Nothing uploaded</div>
                    )}
                  </div>
                  <CardPill tone={group.uploaded ? 'good' : 'neutral'}>
                    {group.uploaded ? `${group.files.filter((file) => file.file_url).length} received` : 'Not uploaded'}
                  </CardPill>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default Recruitment;
